// PDF-rapporten van de module Marktprijzen (22-10-2026).
//
// Vier rapporten, één per tabblad: Actuele prijzen, Gasprijzen, Live onbalans
// en Dagprofiel. Elk rapport is wat er op dat moment op het scherm staat (zelfde
// periode, zelfde reeksen, zelfde cijfers), in de vorm van het meetrapport van
// Metering: compacte kop met logo en kenmerken, kerncijfers op één rij, de
// grafiek (zelf getekend met reportCharts.js — nooit de Recharts-SVG van het
// scherm, die is thema-gekleurd en meet 0×0 in een verborgen tab) en de tabel
// met de cijfers achter de grafiek. De server maakt er een PDF van
// (POST /api/report/pdf, headless Chromium).
//
// Alles hier is tekst-in/tekst-uit: geen React, geen DOM, dus te testen zonder
// browser (src/marktRapport.test.js). Teksten noemen het bronsysteem niet.

import { meetGrafiekSvg } from './reportCharts';
import { gasSerieMeta, gasDekking } from './gasPrijzen';

export const esc = (v) => String(v == null ? '' : v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
// Getal in Belgische notatie met een vast aantal decimalen; leeg = '—'.
export const nl = (v, d = 2) => (v == null || Number.isNaN(Number(v)) ? '—' : Number(v).toLocaleString('nl-BE', { minimumFractionDigits: d, maximumFractionDigits: d }));
export const beDatum = (iso) => { const p = String(iso || '').split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : String(iso || ''); };
const MAAND_KORT = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
export const maandLabel = (jjjjmm) => { const [j, m] = String(jjjjmm).split('-').map(Number); return m >= 1 && m <= 12 ? `${MAAND_KORT[m - 1]} ${j}` : String(jjjjmm); };

// ── Vormgeving ─────────────────────────────────────────────────────────────────
// Dezelfde regels als het meetrapport (buildMeteringReportHtml): A4 staand, kop
// met logo links en titel rechts, kenmerkenbalk, secties met een kleine kop in
// kapitalen, kerncijfertegels met een gekleurde linkerrand.
export function rapportCss(primair, font, kpiKolommen = 3) {
  return `
  :root { --primair: ${esc(primair)}; }
  * { box-sizing: border-box; }
  body { font-family: ${font || "'Segoe UI', system-ui, -apple-system, Arial, sans-serif"}; color: #1e293b; margin: 0; font-size: 11px; line-height: 1.45; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .vel { max-width: 190mm; margin: 0 auto; padding: 6mm 0 12mm; }
  header { display: flex; align-items: center; justify-content: space-between; gap: 24px; padding-bottom: 6px; border-bottom: 3px solid var(--primair); }
  .logo { max-height: 40px; max-width: 200px; object-fit: contain; display: block; }
  .logo-fallback { font-size: 18px; font-weight: 700; color: var(--primair); }
  .kop-rechts { text-align: right; }
  .kop-rechts h1 { margin: 0; font-size: 17px; color: #0f172a; letter-spacing: -0.01em; line-height: 1.2; }
  .kop-rechts .sub { color: #64748b; font-size: 10px; margin-top: 1px; }
  .meta { display: flex; flex-wrap: wrap; margin: 6px 0 8px; border: 1px solid #e2e8f0; border-radius: 6px; overflow: hidden; }
  .meta div { flex: 1 1 25%; min-width: 110px; padding: 4px 10px; border-right: 1px solid #e2e8f0; }
  .meta div:last-child { border-right: 0; }
  .meta .k { color: #64748b; font-size: 8.5px; text-transform: uppercase; letter-spacing: .04em; }
  .meta .v { color: #0f172a; font-weight: 600; font-size: 11px; overflow-wrap: anywhere; }
  .meta .v .muted { font-weight: 400; font-size: 9px; }
  h2 { font-size: 12px; color: #0f172a; margin: 10px 0 6px; padding-bottom: 3px; border-bottom: 1px solid #e2e8f0; text-transform: uppercase; letter-spacing: .05em; }
  .blok:first-of-type h2 { margin-top: 0; }
  .kpis { display: grid; grid-template-columns: repeat(${Math.max(1, Math.min(6, kpiKolommen))}, 1fr); gap: 6px; }
  .kpi { border: 1px solid #e2e8f0; border-left: 3px solid var(--primair); border-radius: 5px; padding: 4px 9px; }
  .kpi-label { color: #64748b; font-size: 8.5px; text-transform: uppercase; letter-spacing: .04em; }
  .kpi-value { color: #0f172a; font-size: 12.5px; font-weight: 700; margin-top: 0; line-height: 1.25; }
  .kpi-sub { color: #64748b; font-size: 8px; margin-top: 1px; }
  .grafiek { border: 1px solid #e2e8f0; border-radius: 8px; padding: 8px 10px 4px; }
  .legende { display: flex; flex-wrap: wrap; gap: 4px 14px; margin: 0 0 6px; font-size: 9.5px; color: #475569; }
  .lg { display: inline-flex; align-items: center; gap: 5px; }
  .stip { width: 8px; height: 8px; border-radius: 50%; display: inline-block; }
  .lijn { width: 14px; height: 2px; display: inline-block; border-radius: 1px; }
  .lijn.streep { background: repeating-linear-gradient(90deg, currentColor 0 4px, transparent 4px 7px) !important; }
  .vlak { width: 12px; height: 8px; display: inline-block; border-radius: 2px; }
  table { width: 100%; border-collapse: collapse; font-size: 10px; }
  th { background: #f1f5f9; text-align: left; padding: 4px 8px; font-weight: 600; color: #334155; border-bottom: 1.5px solid #cbd5e1; white-space: nowrap; }
  td { padding: 3px 8px; border-bottom: 1px solid #eef2f7; }
  th.r, td.r { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
  .kolommen { display: grid; gap: 0 12px; align-items: start; }
  .kolommen table { font-size: 9.5px; }
  .kolommen td, .kolommen th { padding: 2px 6px; }
  .muted { color: #94a3b8; }
  .goed { color: #15803d; }
  .let { color: #b45309; }
  .noot { color: #64748b; font-size: 9.5px; margin: 6px 0 0; }
  .blok { margin-bottom: 10px; }
  footer { margin-top: 12px; padding-top: 6px; border-top: 1px solid #e2e8f0; color: #94a3b8; font-size: 9px; display: flex; justify-content: space-between; }
  @page { size: A4 portrait; margin: 12mm; }
  @media print { .vel { max-width: none; padding: 0; } thead { display: table-header-group; } tr, .kpi, .grafiek { break-inside: avoid; page-break-inside: avoid; } h2 { break-after: avoid; } }`;
}

// Het omhulsel: kop, kenmerkenbalk, inhoud, voet.
// `merk` = { primair, font, logoDataUrl, labelNaam } (al merk-veilig gemaakt door
// de aanroeper); `meta` = [{ k, v }]; `body` = de secties als HTML.
export function rapportHtml({ titel, ondertitel = '', merk = {}, nu = '', meta = [], body = '', voet = '', kpiKolommen = 3 }) {
  const primair = merk.primair || '#4f46e5';
  const labelNaam = merk.labelNaam || '';
  const logo = merk.logoDataUrl
    ? `<img class="logo" src="${esc(merk.logoDataUrl)}" alt="${esc(labelNaam)}" />`
    : `<div class="logo-fallback">${esc(labelNaam)}</div>`;
  return `<!DOCTYPE html>
<html lang="nl">
<head>
<meta charset="UTF-8">
<title>${esc(titel)}</title>
<style>${rapportCss(primair, merk.font, kpiKolommen)}</style>
</head>
<body>
<div class="vel">
  <header>
    <div>${logo}</div>
    <div class="kop-rechts"><h1>${esc(titel)}</h1>${ondertitel ? `<div class="sub">${esc(ondertitel)}</div>` : ''}<div class="sub">Opgemaakt op ${esc(nu)}</div></div>
  </header>
  <div class="meta">${meta.map((m) => `<div><div class="k">${esc(m.k)}</div><div class="v">${m.html ? m.html : esc(m.v)}</div></div>`).join('')}</div>
  ${body}
  <footer><span>${esc(voet || labelNaam)}</span><span>${esc(nu)}</span></footer>
</div>
</body>
</html>`;
}

export const kpisHtml = (kpis) => kpis.map((k) => `<div class="kpi"${k.kleur ? ` style="border-left-color:${esc(k.kleur)}"` : ''}><div class="kpi-label">${esc(k.label)}</div><div class="kpi-value"${k.kleur ? ` style="color:${esc(k.kleur)}"` : ''}>${esc(k.waarde)}</div>${k.sub ? `<div class="kpi-sub">${esc(k.sub)}</div>` : ''}</div>`).join('');

export const legendeHtml = (items) => items.map((l) => l.vlak
  ? `<span class="lg"><span class="vlak" style="background:${esc(l.kleur)};opacity:${l.opacity == null ? 0.25 : l.opacity}"></span>${esc(l.naam)}</span>`
  : `<span class="lg"><span class="lijn${l.streep ? ' streep' : ''}" style="background:${esc(l.kleur)};color:${esc(l.kleur)}"></span>${esc(l.naam)}</span>`).join('');

// Een tabel: `kolommen` = [{ header, rechts?, kleur? }], `rijen` = [[cel, …]] (cellen
// zijn al tekst; een object { html } gaat er rauw in).
export function tabelHtml(kolommen, rijen, leeg = 'Geen gegevens.') {
  const kop = kolommen.map((k) => `<th${k.rechts ? ' class="r"' : ''}${k.kleur ? ` style="color:${esc(k.kleur)}"` : ''}>${esc(k.header)}</th>`).join('');
  const body = rijen.length
    ? rijen.map((r) => `<tr>${r.map((cel, i) => `<td${kolommen[i]?.rechts ? ' class="r"' : ''}>${cel && typeof cel === 'object' && 'html' in cel ? cel.html : esc(cel)}</td>`).join('')}</tr>`).join('')
    : `<tr><td colspan="${kolommen.length}" class="muted">${esc(leeg)}</td></tr>`;
  return `<table><thead><tr>${kop}</tr></thead><tbody>${body}</tbody></table>`;
}

// Lange, smalle tabellen (96 kwartieren, 30 minuten, 90 gasdagen) in `n` kolommen
// naast elkaar, zodat ze op één pagina passen.
export function kolommenHtml(kolommen, rijen, n = 4) {
  if (!rijen.length) return tabelHtml(kolommen, []);
  const stuk = Math.ceil(rijen.length / n);
  const stukken = [];
  for (let i = 0; i < n; i++) { const s = rijen.slice(i * stuk, (i + 1) * stuk); if (s.length) stukken.push(s); }
  return `<div class="kolommen" style="grid-template-columns:repeat(${stukken.length},1fr)">${stukken.map((s) => tabelHtml(kolommen, s)).join('')}</div>`;
}

const sectie = (titel, inhoud) => `<section class="blok"><h2>${esc(titel)}</h2>${inhoud}</section>`;
const grafiekBlok = (legende, svg, noot = '') => `<section class="blok grafiek"><div class="legende">${legende}</div>${svg}${noot ? `<p class="noot">${esc(noot)}</p>` : ''}</section>`;

// Statistiek over een lijst getallen (null overgeslagen).
export function statistiek(waarden) {
  const v = (waarden || []).filter((x) => x != null && Number.isFinite(Number(x))).map(Number);
  if (!v.length) return null;
  const som = v.reduce((a, x) => a + x, 0);
  return { n: v.length, gem: som / v.length, min: Math.min(...v), max: Math.max(...v) };
}

// ── 1. Actuele prijzen ─────────────────────────────────────────────────────────
// `rijen` = de samengevoegde reeks van het scherm (per kwartier: spotEurMwh,
// spotForecastEurMwh, imbalanceEurMwh, imbalanceForecastEurMwh, solarMwActual,
// solarMwForecast, windMwActual, windMwForecast; in weekweergave ook date/time).
export const MP_RAPPORT_KLEUREN = { spot: '#0284c7', onbalans: '#db2777', zon: '#d97706', wind: '#0d9488', live: '#ea580c', forecast: '#7c3aed' };

// Samenvatting per dag (weekweergave): gemiddelde, laagste en hoogste per reeks.
export function dagSamenvatting(rijen) {
  const perDag = new Map();
  for (const r of rijen || []) {
    const d = r.date || String(r.key || '').slice(0, 10);
    if (!perDag.has(d)) perDag.set(d, { date: d, spot: [], onbalans: [] });
    const p = perDag.get(d);
    const s = r.spotEurMwh != null ? r.spotEurMwh : r.spotForecastEurMwh;
    const o = r.imbalanceEurMwh != null ? r.imbalanceEurMwh : r.imbalanceForecastEurMwh;
    if (s != null) p.spot.push(s);
    if (o != null) p.onbalans.push(o);
  }
  return [...perDag.values()].sort((a, b) => a.date.localeCompare(b.date)).map((p) => ({ date: p.date, spot: statistiek(p.spot), onbalans: statistiek(p.onbalans) }));
}

// Maandgemiddelden sinds januari 2024 (uit /api/market/maandgemiddelden): per
// maand het gemiddelde van spot en onbalans, met de dekking in dagen. Zowel op
// het scherm (BarChart) als hier (staven) gebruikt; een maand zonder gegevens
// blijft een gat.
export const maandNaamKort = (jjjjmm) => { const [j, m] = String(jjjjmm).split('-').map(Number); return m >= 1 && m <= 12 ? `${MAAND_KORT[m - 1]} '${String(j).slice(2)}` : String(jjjjmm); };
export function maandenSectieHtml(maanden) {
  const K = MP_RAPPORT_KLEUREN;
  if (!maanden || !maanden.length) return '';
  const metData = maanden.filter((m) => m.spot || m.onbalans);
  if (!metData.length) return sectie('Maandgemiddelden sinds januari 2024', '<p class="muted">Voor deze maanden is nog geen historiek opgehaald.</p>');
  const rijen = maanden.map((m) => ({ key: m.maand, label: maandNaamKort(m.maand), waarden: { spot: m.spot ? m.spot.gem : null, onbalans: m.onbalans ? m.onbalans.gem : null } }));
  const staven = [{ key: 'spot', naam: 'Spotprijs (maandgemiddelde)', kleur: K.spot }, { key: 'onbalans', naam: 'Onbalansprijs (maandgemiddelde)', kleur: K.onbalans }];
  const grafiek = meetGrafiekSvg({ rijen, staven, breedte: 703, hoogte: 240, eenheid: 'EUR/MWh' });
  const dekking = (r, m) => (r ? `${r.dagen}/${m.dagenInMaand}${r.volledig ? '' : ' ·'}` : '—');
  const onvolledig = metData.some((m) => (m.spot && !m.spot.volledig) || (m.onbalans && !m.onbalans.volledig));
  const tabel = kolommenHtml(
    [{ header: 'Maand' }, { header: 'Spot gem.', rechts: true, kleur: K.spot }, { header: 'Onbalans gem.', rechts: true, kleur: K.onbalans }, { header: 'Dagen spot', rechts: true }, { header: 'Dagen onbalans', rechts: true }],
    maanden.map((m) => [maandNaamKort(m.maand), nl(m.spot?.gem), nl(m.onbalans?.gem), dekking(m.spot, m), dekking(m.onbalans, m)]), 2);
  return sectie('Maandgemiddelden sinds januari 2024',
    grafiekBlok(legendeHtml(staven.map((s) => ({ naam: s.naam, kleur: s.kleur, vlak: true, opacity: 1 }))), grafiek,
      `Gemiddelde van alle kwartierwaarden per kalendermaand, in EUR/MWh. Een ontbrekende staaf = geen historiek voor die maand.${onvolledig ? ' Een maand met een · achter de dagen is onvolledig: het gemiddelde gaat over een deel van de maand.' : ''}`)
    + tabel);
}

export function actueelRapport({ merk, nu, weergave = 'day', datum, week = null, rijen = [], toon = {}, aanwezig = {}, stats = {}, spotNu = null, laatsteOnbalans = null, nuKwartier = null, datumLang = '', verouderd = false, maanden = [] }) {
  const K = MP_RAPPORT_KLEUREN;
  const isWeek = weergave === 'week';
  // Maand/Jaar: `rijen` zijn periodegemiddelden ({ key, label, spotEurMwh,
  // imbalanceEurMwh, spot, onbalans, dagenIn }) en worden staven.
  const periodiek = weergave === 'month' || weergave === 'year';
  const periodeWoord = weergave === 'month' ? 'maand' : 'jaar';
  if (periodiek) {
    const staven = [
      ...(toon.spot ? [{ key: 'spotEurMwh', naam: `Spotprijs (gemiddelde per ${periodeWoord})`, kleur: K.spot }] : []),
      ...(toon.onbalans ? [{ key: 'imbalanceEurMwh', naam: `Onbalansprijs (gemiddelde per ${periodeWoord})`, kleur: K.onbalans }] : []),
    ];
    const grafiekRijen = rijen.map((r) => ({ key: r.key, label: r.label || r.key, waarden: r }));
    const grafiek = meetGrafiekSvg({ rijen: grafiekRijen, staven, breedte: 703, hoogte: 290, eenheid: 'EUR/MWh' });
    const kpis = [];
    if (toon.spot && stats.spot) kpis.push(
      { label: `Spot laatste ${periodeWoord}`, waarde: `${nl(spotNu, 2)} EUR/MWh`, kleur: K.spot },
      { label: 'Spot gemiddeld', waarde: `${nl(stats.spot.avg, 1)} EUR/MWh`, kleur: K.spot, sub: `laagste ${periodeWoord} ${nl(stats.spot.min, 2)} · hoogste ${nl(stats.spot.max, 2)}` },
    );
    if (toon.onbalans && stats.onbalans) kpis.push(
      { label: `Onbalans laatste ${periodeWoord}`, waarde: `${nl(stats.onbalans.laatste, 2)} EUR/MWh`, kleur: K.onbalans },
      { label: 'Onbalans gemiddeld', waarde: `${nl(stats.onbalans.avg, 1)} EUR/MWh`, kleur: K.onbalans, sub: `laagste ${periodeWoord} ${nl(stats.onbalans.min, 2)} · hoogste ${nl(stats.onbalans.max, 2)}` },
    );
    const dekking = (r) => (r ? `${r.dagen}${r.volledig ? '' : ' ·'}` : '—');
    const tabel = kolommenHtml(
      [{ header: weergave === 'month' ? 'Maand' : 'Jaar' }, { header: 'Spot gem.', rechts: true, kleur: K.spot }, { header: 'Onbalans gem.', rechts: true, kleur: K.onbalans }, { header: 'Dagen spot', rechts: true }, { header: 'Dagen onbalans', rechts: true }],
      rijen.map((r) => [r.label || r.key, nl(r.spotEurMwh), nl(r.imbalanceEurMwh), dekking(r.spot), dekking(r.onbalans)]), weergave === 'month' ? 2 : 1);
    const periodeTekst = `Gemiddelden per ${periodeWoord} sinds januari 2024`;
    return rapportHtml({
      titel: 'Marktprijzen — Elektriciteitsprijzen (Spot)', ondertitel: periodeTekst, merk, nu, kpiKolommen: Math.max(2, kpis.length),
      meta: [
        { k: 'Periode', v: periodeTekst },
        { k: 'Weergave', v: weergave === 'month' ? 'Maand (gemiddelden)' : 'Jaar (gemiddelden)' },
        { k: 'Reeksen', v: [toon.spot && 'spot', toon.onbalans && 'onbalans'].filter(Boolean).join(', ') || 'geen' },
        { k: weergave === 'month' ? 'Maanden' : 'Jaren', v: String(rijen.length) },
      ],
      body: [
        kpis.length ? sectie('Kerncijfers', `<div class="kpis">${kpisHtml(kpis)}</div>`) : '',
        staven.length && rijen.length
          ? grafiekBlok(legendeHtml(staven.map((s) => ({ naam: s.naam, kleur: s.kleur, vlak: true, opacity: 1 }))), grafiek, `Gemiddelde van alle kwartierwaarden per ${periodeWoord}, in EUR/MWh. Een ontbrekende staaf = geen historiek voor die periode.`)
          : sectie('Grafiek', '<p class="muted">Geen gemiddelden in het prijsarchief.</p>'),
        sectie(`Per ${periodeWoord}`, tabel + `<p class="noot">"Dagen" = het aantal dagen met prijzen in het archief; een · erachter betekent een onvolledige ${periodeWoord}.</p>`),
      ].join(''),
      voet: `${merk?.labelNaam || ''} — day-ahead spotprijs (EPEX BE) en onbalansprijs (Elia), gemiddeld per ${periodeWoord}`,
    });
  }
  const periode = isWeek && week ? `${beDatum(week.from)} – ${beDatum(week.to)}` : (datumLang || beDatum(datum));
  const lijnen = [
    ...(toon.spot && aanwezig.spotActual ? [{ key: 'spotEurMwh', naam: 'Spotprijs', kleur: K.spot, as: 'links' }] : []),
    ...(toon.spot && aanwezig.spotForecast ? [{ key: 'spotForecastEurMwh', naam: 'Spotprijs (forecast)', kleur: K.spot, as: 'links', streep: true }] : []),
    ...(toon.onbalans && aanwezig.imbalanceActual ? [{ key: 'imbalanceEurMwh', naam: 'Onbalansprijs', kleur: K.onbalans, as: 'links' }] : []),
    ...(toon.onbalans && aanwezig.imbalanceForecast ? [{ key: 'imbalanceForecastEurMwh', naam: 'Onbalansprijs (forecast)', kleur: K.onbalans, as: 'links', streep: true }] : []),
    ...(toon.zon && aanwezig.solar ? [{ key: 'zon', naam: 'Zon (GW)', kleur: K.zon, as: 'rechts' }, ...(aanwezig.solarForecast ? [{ key: 'zonF', naam: 'Zon forecast (GW)', kleur: K.zon, as: 'rechts', streep: true }] : [])] : []),
    ...(toon.wind && aanwezig.wind ? [{ key: 'wind', naam: 'Wind (GW)', kleur: K.wind, as: 'rechts' }, ...(aanwezig.windForecast ? [{ key: 'windF', naam: 'Wind forecast (GW)', kleur: K.wind, as: 'rechts', streep: true }] : [])] : []),
  ];
  const gw = (v) => (v == null ? null : Number(v) / 1000);
  const grafiekRijen = rijen.map((r) => {
    const t = r.time || '';
    const label = isWeek ? (t === '00:00' ? beDatum(r.date).slice(0, 5) : '') : (t.endsWith(':00') && Number(t.slice(0, 2)) % 2 === 0 ? t.slice(0, 2) : '');
    return { key: r.key, label, waarden: { ...r, zon: gw(r.solarMwActual), zonF: gw(r.solarMwForecast), wind: gw(r.windMwActual), windF: gw(r.windMwForecast) } };
  });
  const grafiek = meetGrafiekSvg({ rijen: grafiekRijen, lijnen, breedte: 703, hoogte: 290, eenheid: 'EUR/MWh', eenheidRechts: 'GW', grens: !isWeek && nuKwartier && rijen.some((r) => r.key === nuKwartier) ? nuKwartier : null, grensLabel: 'nu' });

  const kpis = [];
  if (toon.spot && (stats.spot || spotNu != null)) {
    kpis.push({ label: 'Spotprijs nu', waarde: `${nl(spotNu, 2)} EUR/MWh`, kleur: K.spot, sub: isWeek ? 'laatst gekende waarde' : '' });
    kpis.push({ label: 'Spotprijs gemiddeld', waarde: `${nl(stats.spot?.avg, 1)} EUR/MWh`, kleur: K.spot, sub: stats.spot ? `laagste ${nl(stats.spot.min, 2)} · hoogste ${nl(stats.spot.max, 2)}` : '' });
  }
  if (toon.onbalans && (stats.onbalans || laatsteOnbalans)) {
    kpis.push({ label: laatsteOnbalans ? `Onbalans nu · ${laatsteOnbalans.time}` : 'Onbalans nu', waarde: `${nl(laatsteOnbalans?.imbalanceEurMwh, 2)} EUR/MWh`, kleur: K.onbalans });
    kpis.push({ label: 'Onbalans gemiddeld', waarde: `${nl(stats.onbalans?.avg, 1)} EUR/MWh`, kleur: K.onbalans, sub: stats.onbalans ? `laagste ${nl(stats.onbalans.min, 2)} · hoogste ${nl(stats.onbalans.max, 2)}` : '' });
  }

  const waarde = (r, a, f) => (r[a] != null ? r[a] : (r[f] != null ? r[f] : null));
  let detail;
  if (isWeek) {
    const dagen = dagSamenvatting(rijen);
    detail = sectie('Per dag', tabelHtml([
      { header: 'Dag' }, { header: 'Spot gem.', rechts: true, kleur: K.spot }, { header: 'Spot min', rechts: true, kleur: K.spot }, { header: 'Spot max', rechts: true, kleur: K.spot },
      { header: 'Onbalans gem.', rechts: true, kleur: K.onbalans }, { header: 'Onbalans min', rechts: true, kleur: K.onbalans }, { header: 'Onbalans max', rechts: true, kleur: K.onbalans },
    ], dagen.map((d) => [beDatum(d.date), nl(d.spot?.gem), nl(d.spot?.min), nl(d.spot?.max), nl(d.onbalans?.gem), nl(d.onbalans?.min), nl(d.onbalans?.max)]), 'Geen prijzen in deze week.'));
  } else {
    detail = sectie('Prijsdetail per kwartier', kolommenHtml([
      { header: 'Tijd' }, { header: 'Spot', rechts: true, kleur: K.spot }, { header: 'Onbalans', rechts: true, kleur: K.onbalans },
    ], rijen.map((r) => [r.time, nl(waarde(r, 'spotEurMwh', 'spotForecastEurMwh')), nl(waarde(r, 'imbalanceEurMwh', 'imbalanceForecastEurMwh'))]), 4)
      + '<p class="noot">Alle prijzen in EUR/MWh. Ontbrekende waarden zijn nog niet gepubliceerd; een forecast telt als waarde zolang de werkelijke prijs er niet is.</p>');
  }

  const reeksen = [toon.spot && 'spot', toon.onbalans && 'onbalans', toon.zon && 'zon', toon.wind && 'wind'].filter(Boolean).join(', ') || 'geen';
  const body = [
    kpis.length ? sectie('Kerncijfers', `<div class="kpis">${kpisHtml(kpis)}</div>`) : '',
    lijnen.length && rijen.length
      ? grafiekBlok(legendeHtml(lijnen.map((l) => ({ naam: l.naam, kleur: l.kleur, streep: l.streep }))), grafiek, `Spot- en onbalansprijs op de linkeras in EUR/MWh; zon en wind op de rechteras in GW. Een streepjeslijn is een forecast.${verouderd ? ' Let op: een deel van de gegevens komt uit een verouderde cache.' : ''}`)
      : sectie('Grafiek', '<p class="muted">Geen prijsdata voor deze periode.</p>'),
    maandenSectieHtml(maanden),
    detail,
  ].join('');

  return rapportHtml({
    titel: 'Marktprijzen — Elektriciteitsprijzen (Spot)', ondertitel: periode, merk, nu, kpiKolommen: Math.max(2, kpis.length),
    meta: [
      { k: 'Periode', v: periode },
      { k: 'Weergave', v: isWeek ? 'Week (kwartieren)' : 'Dag (kwartieren)' },
      { k: 'Reeksen', v: reeksen },
      { k: 'Kwartieren', v: String(rijen.length) },
    ],
    body,
    voet: `${merk?.labelNaam || ''} — day-ahead spotprijs (EPEX BE) en onbalansprijs (Elia), per kwartier`,
  });
}

// ── 2. Gasprijzen ───────────────────────────────────────────────────────────────
// Maandgemiddelden voor lange vensters: een tabel van 1.095 dagwaarden is geen
// rapport meer. Tot en met `MAX_DAGWAARDEN` dagen gaan de dagwaarden zelf erin.
export const MAX_DAGWAARDEN = 124;
export function maandGemiddelden(rijen, types) {
  const per = new Map();
  for (const r of rijen || []) {
    const m = String(r.date).slice(0, 7);
    if (!per.has(m)) per.set(m, { maand: m, waarden: Object.fromEntries(types.map((t) => [t, []])) });
    for (const t of types) if (r[t] != null) per.get(m).waarden[t].push(r[t]);
  }
  return [...per.values()].sort((a, b) => a.maand.localeCompare(b.maand)).map((p) => ({ maand: p.maand, stats: Object.fromEntries(types.map((t) => [t, statistiek(p.waarden[t])])) }));
}

export function gasRapport({ merk, nu, van, tot, dagen, reeksen = [], metData = [], rijen = [], vergelijking = [] }) {
  const lijnen = metData.map((s) => { const m = gasSerieMeta(s.type); return { key: s.type, naam: m?.label || s.type, kleur: m?.kleur || '#334155', as: 'links' }; });
  // Datumlabel om de zoveel dagen, zodat er hooguit ~12 op de as staan.
  const stap = Math.max(1, Math.ceil(rijen.length / 12));
  const grafiekRijen = rijen.map((r, i) => ({ key: r.date, label: i % stap === 0 ? beDatum(r.date).slice(0, 5) : '', waarden: r }));
  const grafiek = meetGrafiekSvg({ rijen: grafiekRijen, lijnen, breedte: 703, hoogte: 270, eenheid: 'EUR/MWh' });

  const kpis = metData.flatMap((s) => {
    const m = gasSerieMeta(s.type);
    return [
      { label: `${m?.label || s.type} laatste`, waarde: `${nl(s.stats?.laatste, 2)} EUR/MWh`, kleur: m?.kleur },
      { label: `${m?.label || s.type} gemiddeld`, waarde: `${nl(s.stats?.gemiddelde, 2)} EUR/MWh`, kleur: m?.kleur, sub: `laagste ${nl(s.stats?.min, 2)} · hoogste ${nl(s.stats?.max, 2)}` },
    ];
  });

  const vergelijkingHtml = vergelijking.length ? sectie('Vergelijking', tabelHtml([
    { header: 'Paar' }, { header: 'Dagen', rechts: true }, { header: 'Gem. verschil', rechts: true }, { header: 'Min', rechts: true }, { header: 'Max', rechts: true }, { header: 'Grootste afwijking' },
  ], vergelijking.map((v) => [v.label, String(v.n), nl(v.gemiddelde, 3), nl(v.min, 3), nl(v.max, 3),
    v.identiek ? { html: '<span class="goed">identieke waarden</span>' } : `${nl(v.grootsteAfwijking, 3)} EUR/MWh op ${beDatum(v.grootsteAfwijkingDag)}`]))
    + '<p class="noot">Verschil = tweede reeks − eerste reeks, over de dagen waarop beide een prijs hebben.</p>') : '';

  const beschikbaarheid = sectie('Beschikbaarheid per reeks', tabelHtml([
    { header: 'Reeks' }, { header: 'Dagen', rechts: true }, { header: 'Van' }, { header: 'Tot' }, { header: 'Opmerking' },
  ], reeksen.map((s) => {
    const m = gasSerieMeta(s.type);
    const d = gasDekking(s);
    const opm = [
      ...(d.dagen > 0 && d.ontbrekend > 0 ? [`${d.ontbrekend} dag(en) zonder prijs (${d.ontbrekendeDagen.map(beDatum).join(', ')}${d.ontbrekend > d.ontbrekendeDagen.length ? '…' : ''})`] : []),
      ...(d.dagen > 0 ? [] : [s.reden || 'geen data']),
      ...(s.waarschuwingen || []),
    ].join(' · ');
    return [m?.label || s.type, String(d.dagen), d.dagen ? beDatum(d.van) : '—', d.dagen ? beDatum(d.tot) : '—', { html: opm ? `<span class="let">${esc(opm)}</span>` : '<span class="muted">—</span>' }];
  })));

  const types = metData.map((s) => s.type);
  let waarden = '';
  if (rijen.length && types.length) {
    if (rijen.length <= MAX_DAGWAARDEN) {
      waarden = sectie(`Dagwaarden (${rijen.length} dagen)`, kolommenHtml(
        [{ header: 'Gasdag' }, ...types.map((t) => ({ header: gasSerieMeta(t)?.label || t, rechts: true, kleur: gasSerieMeta(t)?.kleur }))],
        [...rijen].reverse().map((r) => [beDatum(r.date), ...types.map((t) => nl(r[t], 3))]), types.length > 1 ? 2 : 3));
    } else {
      const maanden = maandGemiddelden(rijen, types);
      waarden = sectie(`Maandgemiddelden (${rijen.length} dagwaarden)`, tabelHtml(
        [{ header: 'Maand' }, ...types.flatMap((t) => [{ header: `${gasSerieMeta(t)?.label || t} gem.`, rechts: true, kleur: gasSerieMeta(t)?.kleur }, { header: 'min', rechts: true }, { header: 'max', rechts: true }])],
        maanden.map((m) => [maandLabel(m.maand), ...types.flatMap((t) => [nl(m.stats[t]?.gem, 3), nl(m.stats[t]?.min, 3), nl(m.stats[t]?.max, 3)])]))
        + `<p class="noot">De dagwaarden zelf (${rijen.length}) staan in de Excel-export van dit tabblad.</p>`);
    }
  }

  const body = [
    kpis.length ? sectie('Kerncijfers', `<div class="kpis">${kpisHtml(kpis)}</div>`) : sectie('Kerncijfers', '<p class="muted">Geen gasprijzen in deze periode.</p>'),
    lijnen.length && rijen.length ? grafiekBlok(legendeHtml(lijnen), grafiek, 'Day-ahead-noteringen (EEX) per prijstype, in EUR/MWh. Een gasdag loopt van 06:00 tot 06:00 lokale tijd; de datum is de dag waarop hij begint.') : '',
    vergelijkingHtml,
    beschikbaarheid,
    waarden,
  ].join('');

  return rapportHtml({
    titel: 'Marktprijzen — Gasprijzen (Spot)', ondertitel: `${beDatum(van)} tot ${beDatum(tot)}`, merk, nu, kpiKolommen: Math.max(2, Math.min(4, kpis.length)),
    meta: [
      { k: 'Periode', v: `${beDatum(van)} tot ${beDatum(tot)}` },
      { k: 'Dagen', v: String(dagen) },
      { k: 'Reeksen', v: metData.map((s) => gasSerieMeta(s.type)?.label || s.type).join(', ') || 'geen' },
      { k: 'Prijs', v: 'Day-ahead per gasdag' },
    ],
    body,
    voet: `${merk?.labelNaam || ''} — gasprijzen TTF en ZTP, day-ahead (EEX), EUR/MWh`,
  });
}

// ── 3. Live onbalans ────────────────────────────────────────────────────────────
export function liveRapport({ merk, nu, kalibratie = null, verouderd = false, laatste = null, forecasts = [], venster = [], nuTijd = null }) {
  const K = MP_RAPPORT_KLEUREN;
  const lijnen = [
    { key: 'imbalanceEurMwh', naam: 'Onbalansprijs (werkelijk)', kleur: K.live, as: 'links' },
    { key: 'forecastPriceEurMwh', naam: 'Forecast (afgeleid)', kleur: K.forecast, as: 'links', streep: true },
  ];
  const stap = Math.max(1, Math.ceil(venster.length / 10));
  const grafiekRijen = venster.map((r, i) => ({ key: r.datetime || r.time, label: i % stap === 0 ? r.time : '', waarden: r }));
  const nuKey = nuTijd ? (venster.find((r) => r.datetime === nuTijd) || {}).datetime : null;
  const grafiek = meetGrafiekSvg({ rijen: grafiekRijen, lijnen, breedte: 703, hoogte: 250, eenheid: 'EUR/MWh', grens: nuKey || null, grensLabel: 'nu' });

  const kpis = [
    ...(laatste ? [{ label: `Laatste onbalans · ${laatste.time}`, waarde: `${nl(laatste.imbalanceEurMwh, 2)} EUR/MWh`, kleur: K.live }] : []),
    ...forecasts.slice(0, 5).map((f) => ({ label: `Forecast · ${f.time}`, waarde: `${nl(f.forecastPriceEurMwh, 2)} EUR/MWh`, kleur: K.forecast })),
  ];
  const stat = statistiek(venster.map((r) => r.imbalanceEurMwh));

  const body = [
    kpis.length ? sectie('Kerncijfers', `<div class="kpis">${kpisHtml(kpis)}</div>`) : sectie('Kerncijfers', '<p class="muted">Nog geen minuutwaarden ontvangen.</p>'),
    venster.length ? grafiekBlok(legendeHtml(lijnen), grafiek, `Minuutwaarden van de laatste 30 minuten, in EUR/MWh.${stat ? ` Gemiddeld ${nl(stat.gem, 2)}, laagste ${nl(stat.min, 2)}, hoogste ${nl(stat.max, 2)} over ${stat.n} minuten.` : ''}${verouderd ? ' Let op: de gegevens komen uit een verouderde cache.' : ''}`) : '',
    venster.length ? sectie('Laatste gegevens', kolommenHtml(
      [{ header: 'Tijd' }, { header: 'Onbalans', rechts: true, kleur: K.live }, { header: 'Forecast', rechts: true, kleur: K.forecast }],
      [...venster].reverse().map((r) => [r.time, nl(r.imbalanceEurMwh, 2), nl(r.forecastPriceEurMwh, 2)]), 2) + '<p class="noot">Nieuwste minuut bovenaan. EUR/MWh.</p>') : '',
  ].join('');

  const kal = kalibratie ? `alpha ${kalibratie.alpha ?? '—'} · marg. inc ${kalibratie.marginalInc ?? '—'} · marg. dec ${kalibratie.marginalDec ?? '—'}` : '—';
  return rapportHtml({
    titel: 'Marktprijzen — Live onbalans', ondertitel: `Momentopname ${nu}`, merk, nu, kpiKolommen: Math.max(2, Math.min(6, kpis.length)),
    meta: [
      { k: 'Momentopname', v: nu },
      { k: 'Venster', v: venster.length ? `${venster[0].time} – ${venster[venster.length - 1].time}` : '—' },
      { k: 'Minuten', v: String(venster.length) },
      { k: 'Kalibratie forecast', v: kal },
    ],
    body,
    voet: `${merk?.labelNaam || ''} — onbalansprijs per minuut (Elia) met afgeleide forecast`,
  });
}

// ── 4. Dagprofiel van de marktprijzen ──────────────────────────────────────────
// Dal = 00–08 en 20–24, piek = 08–20 (dezelfde vlakken als op het scherm).
export const isPiekKwartier = (time) => { const u = Number(String(time).slice(0, 2)); return u >= 8 && u < 20; };
export function profielKerncijfers(rijen) {
  const piek = (rijen || []).filter((r) => isPiekKwartier(r.time));
  const dal = (rijen || []).filter((r) => !isPiekKwartier(r.time));
  const metBelpex = (rijen || []).filter((r) => r.belpexAvg != null);
  let laag = null, hoog = null;
  for (const r of metBelpex) { if (!laag || r.belpexAvg < laag.belpexAvg) laag = r; if (!hoog || r.belpexAvg > hoog.belpexAvg) hoog = r; }
  return {
    belpex: statistiek((rijen || []).map((r) => r.belpexAvg)),
    elia: statistiek((rijen || []).map((r) => r.eliaAvg)),
    belpexPiek: statistiek(piek.map((r) => r.belpexAvg)),
    belpexDal: statistiek(dal.map((r) => r.belpexAvg)),
    eliaPiek: statistiek(piek.map((r) => r.eliaAvg)),
    eliaDal: statistiek(dal.map((r) => r.eliaAvg)),
    laagsteKwartier: laag, hoogsteKwartier: hoog,
  };
}

export function profielRapport({ merk, nu, van, tot, dagen, datapunten = 0, gefaald = 0, rijen = [] }) {
  const K = MP_RAPPORT_KLEUREN;
  const c = profielKerncijfers(rijen);
  const lijnen = [
    { key: 'belpexAvg', naam: 'Spotprijs (gemiddeld)', kleur: K.spot, as: 'links' },
    { key: 'eliaAvg', naam: 'Onbalansprijs (gemiddeld)', kleur: K.onbalans, as: 'links', streep: true },
  ];
  const zones = [
    { van: '00:00', tot: '08:00', kleur: '#f59e0b', opacity: 0.08, label: 'dal' },
    { van: '08:00', tot: '20:00', kleur: '#f43f5e', opacity: 0.06, label: 'piek' },
    { van: '20:00', tot: null, kleur: '#f59e0b', opacity: 0.08, label: 'dal' },
  ];
  const grafiekRijen = rijen.map((r) => ({ key: r.time, label: r.time.endsWith(':00') && Number(r.time.slice(0, 2)) % 2 === 0 ? r.time.slice(0, 2) : '', waarden: r }));
  const grafiek = meetGrafiekSvg({ rijen: grafiekRijen, lijnen, zones, breedte: 703, hoogte: 280, eenheid: 'EUR/MWh' });

  const kpis = [
    { label: 'Spot gemiddeld', waarde: `${nl(c.belpex?.gem, 2)} EUR/MWh`, kleur: K.spot, sub: c.belpexPiek && c.belpexDal ? `piek ${nl(c.belpexPiek.gem, 2)} · dal ${nl(c.belpexDal.gem, 2)}` : '' },
    { label: 'Onbalans gemiddeld', waarde: `${nl(c.elia?.gem, 2)} EUR/MWh`, kleur: K.onbalans, sub: c.eliaPiek && c.eliaDal ? `piek ${nl(c.eliaPiek.gem, 2)} · dal ${nl(c.eliaDal.gem, 2)}` : '' },
    { label: 'Goedkoopste kwartier (spot)', waarde: c.laagsteKwartier ? `${c.laagsteKwartier.time} · ${nl(c.laagsteKwartier.belpexAvg, 2)}` : '—', kleur: K.spot, sub: 'gemiddelde over de periode' },
    { label: 'Duurste kwartier (spot)', waarde: c.hoogsteKwartier ? `${c.hoogsteKwartier.time} · ${nl(c.hoogsteKwartier.belpexAvg, 2)}` : '—', kleur: K.spot, sub: 'gemiddelde over de periode' },
  ];

  const body = [
    sectie('Kerncijfers', `<div class="kpis">${kpisHtml(kpis)}</div>`),
    rijen.length ? grafiekBlok(legendeHtml([...lijnen, { naam: 'Daluren (00–08 en 20–24)', kleur: '#f59e0b', vlak: true }, { naam: 'Piekuren (08–20)', kleur: '#f43f5e', vlak: true }]), grafiek,
      `Gemiddelde per kwartier-van-de-dag over ${dagen} dagen (${datapunten} datapunten${gefaald > 0 ? `, ${gefaald} dag(en) niet opgehaald` : ', alle dagen volledig'}).`) : sectie('Grafiek', '<p class="muted">Geen prijzen in deze periode.</p>'),
    rijen.length ? sectie('Gemiddelde per kwartier', kolommenHtml(
      [{ header: 'Tijd' }, { header: 'Spot', rechts: true, kleur: K.spot }, { header: 'Onbalans', rechts: true, kleur: K.onbalans }, { header: 'Dagen', rechts: true }],
      rijen.map((r) => [r.time, nl(r.belpexAvg, 2), nl(r.eliaAvg, 2), String(r.n ?? '')]), 4) + '<p class="noot">EUR/MWh. "Dagen" = het aantal dagen met een waarde voor dat kwartier.</p>') : '',
  ].join('');

  return rapportHtml({
    titel: 'Marktprijzen — Dagprofiel', ondertitel: `${beDatum(van)} tot ${beDatum(tot)}`, merk, nu, kpiKolommen: 4,
    meta: [
      { k: 'Periode', v: `${beDatum(van)} tot ${beDatum(tot)}` },
      { k: 'Dagen', v: String(dagen) },
      { k: 'Datapunten', v: String(datapunten) },
      { k: 'Resolutie', v: 'Kwartier van de dag' },
    ],
    body,
    voet: `${merk?.labelNaam || ''} — gemiddeld dagprofiel van spot- en onbalansprijs`,
  });
}

// ── 5. Forward Market ───────────────────────────────────────────────────────────
// Beide producten (Power BE Baseload, TTF Gas NL Baseload) met precies de
// contracten van de ladder op het scherm (x maanden → y kwartalen → Cal's erna),
// in dezelfde kleuren. `grafieken` = [{ label, unit, chart: [{ tradeDate,
// tradeDateLabel, <contract>: prijs }], verborgen: [contract] }].
export const laatstGekend = (chart, naam) => {
  for (let i = (chart || []).length - 1; i >= 0; i--) { const v = chart[i][naam]; if (v != null) return { waarde: v, label: chart[i].tradeDateLabel, datum: chart[i].tradeDate }; }
  return null;
};
export const eerstGekend = (chart, naam) => {
  for (const r of chart || []) { if (r[naam] != null) return { waarde: r[naam], label: r.tradeDateLabel, datum: r.tradeDate }; }
  return null;
};
// Tabel Maandprijzen (zoals op het scherm en in de export): per leveringsmaand de
// fijnste notering en CALC per product; historische maanden met hun eigen notering.
// `maandprijzen` = { rijen: [{ periode, historisch?, prijzen: {id → {waarde, contract,
// soort, notering?}}, calc: {id → {waarde, basis}} }], producten: [{ id, label,
// notering }], huidigeMaand: 'YYYY-MM' }.
const SOORT_KORT = { maand: 'M', kwartaal: 'Q', jaar: 'Cal' };
export function maandprijzenSectieHtml(maandprijzen, kleuren = []) {
  if (!maandprijzen || !maandprijzen.rijen?.length || !maandprijzen.producten?.length) return '';
  const { rijen, producten, huidigeMaand } = maandprijzen;
  const kleur = (i) => kleuren[i % Math.max(1, kleuren.length)] || '#334155';
  const kolommen = [{ header: 'Maand' }, ...producten.flatMap((p, i) => [
    { header: `${p.label}`, rechts: true, kleur: kleur(i) }, { header: 'CALC', rechts: true, kleur: kleur(i) }, { header: 'Contract' },
  ])];
  const tabelRijen = rijen.map((r) => {
    const huidig = r.periode === huidigeMaand;
    const naam = `${esc(r.periode)}${huidig ? ' <span class="goed">nu</span>' : ''}${r.historisch ? ' <span class="muted">historisch</span>' : ''}`;
    return [{ html: huidig ? `<strong>${naam}</strong>` : naam }, ...producten.flatMap((p) => {
      const prijs = r.prijzen?.[p.id], calc = r.calc?.[p.id];
      const verdeeld = calc && calc.basis && !/ongewijzigd/.test(calc.basis);
      return [
        nl(prijs?.waarde),
        calc ? { html: `<span${verdeeld ? ' class="goed"' : ''} title="${esc(calc.basis || '')}">${nl(calc.waarde)}</span>` } : '—',
        prijs ? `${prijs.contract} (${SOORT_KORT[prijs.soort] || prijs.soort})${r.historisch && prijs.notering ? ` · ${beDatum(prijs.notering)}` : ''}` : { html: '<span class="muted">geen notering</span>' },
      ];
    })];
  });
  const historisch = rijen.filter((r) => r.historisch).length;
  const noteringen = producten.filter((p) => p.notering).map((p) => `${p.label}: ${beDatum(p.notering)}`).join(' · ');
  return sectie(`Maandprijzen — meest recente notering (${rijen.length} maanden${historisch ? `, waarvan ${historisch} historisch` : ''})`,
    `<p class="noot" style="margin:0 0 6px">Per leveringsmaand de fijnste notering: maandcontract (M), anders kwartaal (Q), anders kalenderjaar (Cal). CALC in groen = kwartaal- of Cal-prijs verdeeld volgens het profiel van de laatst gekende maandprijzen. EUR/MWh${noteringen ? ` · notering ${esc(noteringen)}` : ''}.</p>`
    + tabelHtml(kolommen, tabelRijen)
    + (historisch ? '<p class="noot">Historische maanden tonen de laatste notering van hun eigen maandcontract, met die datum erbij.</p>' : ''));
}

export function forwardRapport({ merk, nu, dagen, ladder = { maanden: 3, kwartalen: 3 }, contracten = [], kleuren = [], grafieken = [], maandprijzen = null }) {
  const kleur = (naam) => kleuren[Math.max(0, contracten.indexOf(naam)) % Math.max(1, kleuren.length)] || '#334155';
  const blokken = grafieken.map((g) => {
    const chart = g.chart || [];
    const zichtbaar = contracten.filter((c) => !(g.verborgen || []).includes(c));
    const laatsteDag = chart.length ? chart[chart.length - 1].tradeDateLabel : '';
    const kpis = contracten.map((c) => {
      const l = laatstGekend(chart, c), e = eerstGekend(chart, c);
      const delta = l && e && e.waarde ? ((l.waarde - e.waarde) / Math.abs(e.waarde)) * 100 : null;
      return { label: c, waarde: nl(l?.waarde, 2), kleur: kleur(c), sub: l ? `${l.label}${delta != null ? ` · ${delta > 0 ? '+' : ''}${nl(delta, 1)} % over de periode` : ''}` : 'geen notering' };
    });
    const stap = Math.max(1, Math.ceil(chart.length / 10));
    const rijen = chart.map((r, i) => ({ key: r.tradeDate || r.tradeDateLabel, label: i % stap === 0 || i === chart.length - 1 ? r.tradeDateLabel : '', waarden: r }));
    const lijnen = zichtbaar.map((c) => ({ key: c, naam: c, kleur: kleur(c), as: 'links' }));
    const grafiek = lijnen.length && chart.length
      ? meetGrafiekSvg({ rijen, lijnen, breedte: 703, hoogte: 250, eenheid: g.unit || 'EUR/MWh', asVanNul: false })
      : '';
    const tabel = tabelHtml(
      [{ header: 'Contract' }, { header: 'Laatste prijs', rechts: true }, { header: 'Notering' }, { header: 'Begin periode', rechts: true }, { header: 'Verschil', rechts: true }, { header: 'Laagste', rechts: true }, { header: 'Hoogste', rechts: true }],
      contracten.map((c) => {
        const l = laatstGekend(chart, c), e = eerstGekend(chart, c);
        const s = statistiek(chart.map((r) => r[c]));
        const delta = l && e && e.waarde ? l.waarde - e.waarde : null;
        return [{ html: `<span style="color:${esc(kleur(c))};font-weight:600">${esc(c)}</span>` }, nl(l?.waarde), l ? l.label : '—', nl(e?.waarde), delta == null ? '—' : `${delta > 0 ? '+' : ''}${nl(delta)}`, nl(s?.min), nl(s?.max)];
      }), 'Geen noteringen in deze periode.');
    return `<section class="blok"><h2>${esc(g.label)}</h2>
    <p class="noot" style="margin:0 0 6px">Laatst gekende prijzen${laatsteDag ? ` · notering ${esc(laatsteDag)}` : ''} · ${esc(g.unit || 'EUR/MWh')}${(g.verborgen || []).length ? ` · verborgen in de grafiek: ${esc(g.verborgen.join(', '))}` : ''}</p>
    <div class="kpis" style="grid-template-columns:repeat(${Math.min(8, Math.max(2, contracten.length))},1fr)">${kpisHtml(kpis)}</div>
    ${grafiek ? grafiekBlok(legendeHtml(lijnen), grafiek) : '<p class="muted">Geen forward data voor dit product.</p>'}
    ${tabel}
  </section>`;
  }).join('');

  return rapportHtml({
    titel: 'Marktprijzen — Forward Market', ondertitel: `laatste ${dagen} dagen · ${contracten.length} contracten`, merk, nu,
    meta: [
      { k: 'Periode', v: `laatste ${dagen} handelsdagen` },
      { k: 'Ladder', v: `${ladder.maanden} maand${ladder.maanden === 1 ? '' : 'en'} · ${ladder.kwartalen} kwarta${ladder.kwartalen === 1 ? 'al' : 'len'} · Cal erna` },
      { k: 'Contracten', v: contracten.join(', ') || 'geen' },
      { k: 'Producten', v: grafieken.map((g) => g.label).join(' · ') },
    ],
    body: (blokken || '<p class="muted">Geen forward data beschikbaar.</p>') + maandprijzenSectieHtml(maandprijzen, kleuren),
    voet: `${merk?.labelNaam || ''} — termijnprijzen baseload (ICE Endex), EUR/MWh`,
  });
}
